<template>
  <div>
    <PageHeaderLayout>
      <div class="main-page-content">
        <el-card shadow="never" class="app-pkg-page-card">
          <div class="backup-header">
            <i class="el-icon-mobile-phone"></i>
            <span>App 安装包管理</span>
          </div>
          <div class="backup-desc">
            安装包保存在服务器本机（admin_releases/），不上传 R2。<br>
            二维码为持久链接：不过期、可反复扫码。重新上传后，同一二维码自动指向最新安装包。
          </div>

          <el-alert
            v-if="!apiBase"
            type="info"
            :closable="false"
            class="same-origin-tip"
            title="当前为同源部署：二维码使用站点域名生成，手机可直接访问。"
          />

          <el-row :gutter="16" class="pkg-row">
            <el-col :span="12" v-for="pkg in packages" :key="pkg.platform">
              <div class="pkg-card">
                <div class="pkg-title">
                  <i :class="pkg.platform === 'android' ? 'el-icon-mobile-phone' : 'el-icon-monitor'"></i>
                  {{ pkg.platform === 'android' ? 'Android（APK）' : 'Windows（ZIP）' }}
                </div>
                <p class="pkg-meta" v-if="pkg.ready">
                  已上传 · {{ formatSize(pkg.size_bytes) }} · {{ formatTime(pkg.updated_at) }}
                </p>
                <p class="pkg-meta pkg-missing" v-else>尚未上传</p>

                <div class="pkg-actions">
                  <el-upload
                    :action="uploadAction"
                    :headers="uploadHeaders"
                    :data="{ platform: pkg.platform }"
                    :show-file-list="false"
                    :accept="pkg.platform === 'android' ? '.apk' : '.zip'"
                    :before-upload="(f) => beforeUpload(f, pkg.platform)"
                    :on-success="(res) => onUploadSuccess(res, pkg.platform)"
                    :on-error="onUploadError">
                    <el-button size="mini" type="primary" plain icon="el-icon-upload2">
                      {{ pkg.ready ? '上传替换' : '上传安装包' }}
                    </el-button>
                  </el-upload>
                  <el-button
                    size="mini"
                    type="success"
                    icon="el-icon-download"
                    :disabled="!pkg.ready"
                    :loading="downloading === pkg.platform"
                    @click="directDownload(pkg.platform)">下载到本机</el-button>
                  <el-button
                    size="mini"
                    icon="el-icon-full-screen"
                    :disabled="!pkg.ready"
                    @click="showQr(pkg.platform)">生成二维码</el-button>
                </div>
              </div>
            </el-col>
          </el-row>

          <div v-if="qrVisible" class="qr-box">
            <canvas ref="qrCanvas" width="220" height="220"></canvas>
            <p class="qr-caption">持久链接 · 不过期 · 可重复扫码下载</p>
            <div class="qr-actions">
              <el-button type="text" icon="el-icon-download" @click="downloadQrImage">下载二维码图片</el-button>
              <el-button type="text" icon="el-icon-copy-document" @click="copyQrLink">复制链接</el-button>
              <el-button type="text" icon="el-icon-refresh" @click="showQr(qrPlatform, true)">刷新二维码</el-button>
            </div>
          </div>
        </el-card>
      </div>
    </PageHeaderLayout>
  </div>
</template>

<script>
import PageHeaderLayout from '@/layouts/PageHeaderLayout'
import { buildUploadAuthHeaders } from '@/utils/request'
import { renderQrToCanvas } from '@/utils/qrcode'

const API_BASE = (process.env.VUE_APP_AXIOS_BASE_URL || '').replace(/\/$/, '')

export default {
  components: {
    PageHeaderLayout
  },
  data() {
    return {
      packages: [
        { platform: 'android', ready: false, size_bytes: 0, updated_at: null, file_name: 'ola-admin.apk' },
        { platform: 'windows', ready: false, size_bytes: 0, updated_at: null, file_name: 'ola-admin-windows.zip' }
      ],
      uploadAction: '/service/admin_app/upload',
      uploadingPlatform: '',
      downloading: '',
      qrVisible: false,
      qrPlatform: '',
      qrUrl: ''
    }
  },
  computed: {
    apiBase() {
      return API_BASE
    }
  },
  methods: {
    formatSize(n) {
      const b = Number(n) || 0
      if (b < 1024) return b + ' B'
      if (b < 1048576) return (b / 1024).toFixed(1) + ' KB'
      return (b / 1048576).toFixed(1) + ' MB'
    },
    formatTime(t) {
      if (!t) return ''
      const d = new Date(t)
      if (Number.isNaN(d.getTime())) return String(t)
      return d.toLocaleString()
    },
    beforeUpload(file, platform) {
      const max = 128 * 1024 * 1024
      const ext = (file.name || '').toLowerCase()
      if (file.size > max) {
        this.$message.error('文件不能超过 128MB')
        return false
      }
      if (platform === 'android' && !ext.endsWith('.apk')) {
        this.$message.error('Android 平台只能上传 .apk')
        return false
      }
      if (platform === 'windows' && !ext.endsWith('.zip')) {
        this.$message.error('Windows 平台只能上传 .zip（Release 目录打包）')
        return false
      }
      this.uploadingPlatform = platform
      return true
    },
    onUploadSuccess(res, platform) {
      this.uploadingPlatform = ''
      if (res && (res.code === 200 || res.code === '200')) {
        this.$message.success('上传成功，持久二维码已自动指向新包')
        this.loadStatus()
        if (this.qrVisible && this.qrPlatform === platform) {
          this.showQr(platform, true)
        }
      } else {
        this.$message.error((res && res.message) || '上传失败')
      }
    },
    onUploadError() {
      this.uploadingPlatform = ''
      this.$message.error('上传失败')
    },
    async loadStatus() {
      try {
        const res = await this.$api.post('/service/admin_app/status', {})
        if (res && res.code === 200 && res.data && res.data.packages) {
          const list = res.data.packages
          if (list.length) {
            this.packages = list.map(p => ({
              platform: p.platform,
              ready: !!p.ready,
              size_bytes: Number(p.size_bytes) || 0,
              updated_at: p.updated_at || null,
              file_name: p.file_name || ''
            }))
          }
        }
      } catch (e) { /* 静默 */ }
    },
    async fetchPersistentLink(platform) {
      const res = await this.$api.post('/service/admin_app/persistent_download', { platform })
      if (!(res && res.code === 200)) {
        throw new Error((res && res.message) || '生成下载链接失败')
      }
      const d = res.data || {}
      const path = d.download_url || ''
      // 二维码必须绝对 URL：同源部署时 API_BASE 为空串，用当前 origin 兜底
      return path.startsWith('http') ? path : ((API_BASE || window.location.origin) + path)
    },
    async directDownload(platform) {
      this.downloading = platform
      try {
        const abs = await this.fetchPersistentLink(platform)
        window.open(abs, '_blank', 'noopener')
        this.$message.success('开始下载')
      } catch (e) {
        this.$message.error((e && e.message) || '请求失败')
      }
      this.downloading = ''
    },
    async showQr(platform, refresh) {
      try {
        if (!refresh) this.qrPlatform = platform
        const abs = await this.fetchPersistentLink(platform)
        this.qrUrl = abs
        this.qrVisible = true
        this.$nextTick(() => {
          const canvas = this.$refs.qrCanvas
          if (!canvas) return
          try {
            renderQrToCanvas(canvas, abs, { size: 220, margin: 2, level: 'M' })
          } catch (e) {
            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.fillStyle = '#f56c6c'
            ctx.font = '12px sans-serif'
            ctx.textAlign = 'center'
            ctx.fillText('地址过长，无法生成', canvas.width / 2, canvas.height / 2)
          }
        })
      } catch (e) {
        this.$message.error((e && e.message) || '请求失败')
      }
    },
    downloadQrImage() {
      const canvas = this.$refs.qrCanvas
      if (!canvas || !this.qrUrl) return
      const link = document.createElement('a')
      link.download = 'ola-admin-app-qr-' + this.qrPlatform + '.png'
      link.href = canvas.toDataURL('image/png')
      link.click()
    },
    copyQrLink() {
      if (!this.qrUrl) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.qrUrl).then(() => {
          this.$message.success('链接已复制')
        }).catch(() => {
          this.fallbackCopy()
        })
      } else {
        this.fallbackCopy()
      }
    },
    fallbackCopy() {
      const input = document.createElement('input')
      input.value = this.qrUrl
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$message.success('链接已复制')
    }
  },
  async mounted() {
    this.uploadHeaders = buildUploadAuthHeaders()
    await this.loadStatus()
  }
}
</script>

<style lang="stylus" scoped>
.app-pkg-page-card
  max-width 960px
.backup-header
  font-size 18px
  font-weight 600
  display flex
  align-items center
  i
    margin-right 8px
    color #409EFF
.backup-desc
  margin 10px 0 4px
  font-size 13px
  color #909399
  line-height 1.6
.same-origin-tip
  margin-top 10px
.pkg-row
  margin-top 16px
.pkg-card
  border 1px solid #ebeef5
  border-radius 8px
  padding 14px 16px
  background #fafafa
  min-height 140px
.pkg-title
  font-weight 600
  margin-bottom 8px
  i
    margin-right 6px
    color #409EFF
.pkg-meta
  margin 0 0 10px
  font-size 12px
  color #909399
  &.pkg-missing
    color #f56c6c
.pkg-actions
  display flex
  flex-wrap wrap
  gap 8px
  align-items center
.qr-box
  margin-top 20px
  display flex
  flex-direction column
  align-items center
  gap 8px
  canvas
    border 1px solid #ebeef5
    border-radius 8px
    background #fff
.qr-caption
  margin 0
  font-size 12px
  color #67c23a
.qr-actions
  display flex
  gap 12px
</style>
